<script setup>
import IconButton from "@/components/IconButton.vue";
import IconBlock from "@/components/IconBlock.vue";
import { ref } from "vue";

const mobileNavToggled = ref(false);
function toggleMobileNav() {
  mobileNavToggled.value = !mobileNavToggled.value;
}
</script>

<template>
  <div class="page-background"></div>
  <header class="header gutter-top">
    <div class="main-container">
      <div class="d-flex align-items-center justify-content-between justify-content-lg-center">
        <img src="/images/ashley-friel-logo.png" alt="Ashley Friel Logo" class="header-logo" />
        <button @click="toggleMobileNav" class="mobile-nav-toggler d-flex d-lg-none">
          <i class="bi bi-list"></i>
        </button>
      </div>
    </div>
  </header>
  <main class="main gutter-top gutter-bottom">
    <div class="main-container">
      <div class="main-grid">
        <aside class="main-sidebar" ref="sidebarContainer">
          <div class="main-sidebar-sticky bg-white rounded-4 shadow-sm" ref="sidebar">
            <div class="text-center">
              <img
                src="/images/ashley-friel-headshot.jpg"
                alt="Ashley Friel Headshot"
                class="main-sidebar-image rounded-4"
              />
            </div>
            <div class="d-flex flex-column align-items-center text-center p-4">
              <h1 class="mb-0">Ashley Friel</h1>
              <span class="small bg-light px-3 py-1 rounded-4 mt-3">Business Student</span>
              <div class="d-flex align-items-center justify-content-center gap-3 mt-3">
                <IconButton link="https://instagram.com/ashley.friel2" icon="bi bi-instagram" />
                <IconButton link="https://x.com/aafriel_22" icon="bi bi-twitter-x" />
                <IconButton link="https://ca.linkedin.com/in/ashley-friel-98711a328" icon="bi bi-linkedin" />
              </div>
              <div class="d-flex flex-column align-items-start bg-light p-3 rounded-4 w-100 mt-4">
                <IconBlock icon="bi bi-house" title="LOCATION" subtitle="Innisfil, ON" />
                <IconBlock
                  icon="bi bi-envelope"
                  title="EMAIL"
                  subtitle="aafriel22@icloud.com"
                  class="border-top mt-3 pt-3"
                />
                <IconBlock
                  icon="bi bi-telephone"
                  title="PHONE"
                  subtitle="+1 (905) 407-5222"
                  class="border-top mt-3 pt-3"
                />
                <IconBlock
                  icon="bi bi-calendar-date"
                  title="BIRTHDAY"
                  subtitle="Sept. 29, 2000"
                  class="border-top mt-3 pt-3"
                />
              </div>
            </div>
          </div>
        </aside>
        <div class="main-content bg-white p-4 p-lg-5 rounded-4 shadow-sm">
          <nav class="main-nav d-none d-lg-flex">
            <router-link :to="{ name: 'HomeView' }">Home</router-link>
            <router-link :to="{ name: 'ResumeView' }">Resume</router-link>
            <router-link :to="{ name: 'BlogView' }">Blog</router-link>
            <router-link :to="{ name: 'ContactView' }">Contact</router-link>
          </nav>
          <div class="main-content-inner">
            <router-view />
          </div>
        </div>
      </div>
    </div>
  </main>
  <footer class="footer border-top">
    <div class="container-fluid container-lg text-center py-3">
      <p class="copyright mb-0">&copy; Ashley Friel, 2025. All rights reserved.</p>
    </div>
  </footer>

  <div class="mobile-nav" :class="{ active: mobileNavToggled }">
    <div class="d-flex align-items-center justify-content-between">
      <img src="/images/ashley-friel-logo-white.png" alt="Ashley Friel Logo (White)" class="mobile-nav-logo" />
      <button @click="toggleMobileNav" class="mobile-nav-toggler mobile-nav-toggler-inner">
        <i class="bi bi-list"></i>
      </button>
    </div>
    <nav class="mt-5">
      <router-link @click="toggleMobileNav" :to="{ name: 'HomeView' }">Home</router-link>
      <router-link @click="toggleMobileNav" :to="{ name: 'ResumeView' }">Resume</router-link>
      <router-link @click="toggleMobileNav" :to="{ name: 'BlogView' }">Blog</router-link>
      <router-link @click="toggleMobileNav" :to="{ name: 'ContactView' }">Contact</router-link>
    </nav>
  </div>
</template>

<style lang="scss">
.page-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-image: url("/public/images/ashley-friel-page-background.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.065;
  z-index: -1;
}

.header-logo {
  width: 150px;
  height: auto;

  @media (max-width: 992px) {
    width: 100px;
  }
}

.main {
  &-container {
    max-width: 1450px;
    margin: 0 auto;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  &-grid {
    display: grid;
    grid-template-columns: 385px 1fr;
    grid-gap: 30px;
    align-items: start;

    @media (max-width: 1450px) {
      grid-template-columns: 300px 1fr;
    }

    @media (max-width: 1200px) {
      grid-template-columns: 250px 1fr;
    }

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  .main-sidebar {
    position: relative;
    height: 100%;

    &-sticky {
      position: sticky;
      top: 8rem;
    }

    &-image {
      width: 235px;
      height: 235px;
      margin-top: -7rem;

      @media (max-width: 1200px) {
        width: 175px;
        height: 175px;
        margin-top: -4rem;
      }

      @media (max-width: 992px) {
        margin-top: 3rem;
        width: 235px;
        height: 235px;
      }
    }
  }

  .main-content {
    position: relative;

    &-inner {
      margin-top: 2rem;
    }

    @media (max-width: 992px) {
      &-inner {
        margin-top: 0;
      }
    }
  }

  .main-nav {
    position: absolute;
    top: 0;
    right: 0;
    height: 50px;

    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 1rem;
    padding: 0 50px;
    background-color: var(--bc-primary);
    border-top-right-radius: 1rem;
    border-bottom-left-radius: 1rem;
    transition: width 0.3s ease;

    a {
      color: rgba(255, 255, 255, 0.7);
      text-decoration: none;
      font-weight: 500;
      text-transform: uppercase;
      transition: color 0.3s ease;

      &.router-link-active,
      &:hover {
        color: white;
      }
    }
  }
}

.copyright {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.1);
}

.mobile-nav {
  position: fixed;
  top: 0;
  right: -100%;
  width: 100%;
  height: 100vh;
  padding: 2rem;
  background-color: var(--bc-primary);
  z-index: 2000;
  transition: right 0.3s ease;

  &.active {
    right: 0;
  }

  @media (min-width: 992px) {
    right: -100% !important;
  }

  &-logo {
    width: 80px;
  }

  nav {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;

    a {
      color: rgba(255, 255, 255, 0.7);
      font-size: 2rem;
      text-decoration: none;
      text-transform: uppercase;

      &.router-link-active,
      &:hover {
        color: white;
      }

      &:active {
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }

  &-toggler {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 45px;
    height: 45px;
    border-radius: 0.5rem;
    border: none;
    background-color: var(--bc-primary);
    transition: background-color 0.3s ease;

    &-inner {
      background-color: rgba(255, 255, 255, 0.1);

      &:hover {
        background-color: rgba(255, 255, 255, 0.3) !important;
      }

      &:active {
        background-color: rgba(255, 255, 255, 0.1) !important;
      }
    }

    &:hover {
      background-color: var(--bc-primary-alt);
    }

    &:active {
      background-color: var(--bc-primary);
    }

    i {
      color: white;
      font-size: 30px;
    }
  }
}
</style>
